/**
 * What a screen reads the device cache through. P7's Home renders `gates` and calls
 * `statusPhase()` per card; it never opens the store, and it never sees a raw connectivity
 * value it could mistake for a fresh one.
 */

import { createContext, useContext } from 'react'
import type { GatePosition } from '@selogate/core'
import type { CachedGate, SnapshotOrigin, StatusPhase } from './snapshot'

export interface DeviceCacheApi {
  readonly gates: readonly CachedGate[]
  /** `'restored'` until this launch's `GET /gates` returns. `undefined` before either. */
  readonly origin: SnapshotOrigin | undefined
  /**
   * When this launch's `GET /gates` last produced a snapshot, ISO-8601. **`undefined` means the
   * list has never been fetched on this device for this account** — which is what tells an empty
   * `gates` array apart from "you have no gates".
   */
  readonly fetchedAt?: string
  readonly refreshing: boolean
  /**
   * **R1-8 — whether the most recent `refresh()` failed** (a non-200, a `NetworkError`, or any
   * other throw). `false` before the first attempt and after every success.
   *
   * It exists so a screen can name the three cases apart, which nothing on this context could
   * do before: `fetchedAt === undefined && lastRefreshFailed` is *we asked and could not get an
   * answer*; `fetchedAt === undefined && !lastRefreshFailed` is *we have not asked yet*; and
   * `fetchedAt !== undefined` with an empty `gates` is the only one of the three that means
   * **you have no gates**. Rendering the empty state for the first two is a claim, and it is
   * one a paying resident on a cold offline launch was being told.
   *
   * **Optional only so that a test double written before R1 still satisfies this interface** —
   * `DeviceCacheProvider` always supplies it, and `undefined` reads exactly as `false` does.
   */
  readonly lastRefreshFailed?: boolean
  /** Offline read-only: **no cloud action may be offered**, optimistically or otherwise. */
  readonly readOnly: boolean
  readonly online: boolean
  /**
   * The connectivity phase for one gate. `fresh` requires a probe from this launch — see
   * `snapshot.ts`. **No longer "the LED" since D243**, which deleted the dot; Gate Detail reads
   * this to decide whether its wifi glyph is verified-green, and the Install screen reads it for
   * check 1. The evidence rule is unchanged.
   */
  statusPhase(gateId: string): StatusPhase
  /**
   * The gate's reported position as of a probe **in this launch**, or `undefined` when nothing
   * has verified it (D134). Same evidence rule as {@link statusPhase}: a screen may fall back to
   * the cached `lastPosition`, but must present that as last-known rather than current.
   */
  livePosition(gateId: string): GatePosition | undefined
  refresh(): Promise<void>
  /**
   * The app bar's refresh control: `refresh()` then the same probe sweep a reconnect runs, in
   * that order. **Not a second refresh policy** — it is `runRefreshProbeCycle` under a user's
   * finger, so a manual refresh and a returning connection reconcile the cache identically.
   * Probing follows `gatesNeedingProbe`, so a gate already answering `online` in this launch is
   * not re-addressed and D68's limiter is not walked into for nothing.
   */
  refreshAll(): Promise<void>
  /**
   * @param confirmFor the `idempotencyKey` of an open this probe is confirming (D134). It claims
   * the shorter confirm interval so the caller who just opened can watch the gate move; an
   * unusable claim is ignored server-side and the probe answers normally.
   */
  probeGate(gateId: string, confirmFor?: string): Promise<void>
  /**
   * Applies a successful personalization save to the in-memory and durable snapshots for one
   * gate, so Home reflects it immediately rather than waiting for the next `refresh()`. Only
   * the caller's own successful `PATCH /gates/{id}/view` should call this — a failed save must
   * leave the cache untouched, and the next server refresh remains authoritative either way.
   *
   * `sortOrder` is the one field of this patch Home writes for every gate on a reorder, not
   * just the dragged one — without it here, a reorder only ever reached the server, and a
   * restore from this device's own snapshot (a reload before the next `GET /gates`) put every
   * tile back where the *last-cached* `sortOrder` had them.
   */
  updateGateView(
    gateId: string,
    patch: {
      readonly alias?: string
      readonly icon?: string
      readonly color?: string
      readonly sortOrder?: number
    },
  ): Promise<void>
}

export const DeviceCacheContext = createContext<DeviceCacheApi | undefined>(undefined)

export function useDeviceCache(): DeviceCacheApi {
  const ctx = useContext(DeviceCacheContext)
  if (ctx === undefined) throw new Error('useDeviceCache() called outside <DeviceCacheProvider>')
  return ctx
}

/**
 * The same read, for **chrome rather than a screen**: `undefined` when no provider is mounted.
 *
 * A screen that cannot find the cache is a bug and should throw — it has nothing to render. The
 * app bar is the opposite case: it is P6's, it renders above the data layer, and its refresh
 * control is one affordance among several. Not rendering it is the correct degradation, and it
 * is what keeps `AppBar` unit-testable without standing up a cache.
 */
export function useOptionalDeviceCache(): DeviceCacheApi | undefined {
  return useContext(DeviceCacheContext)
}
